import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from 'oidc-client-ts';
import {
  AuthProvider as OidcProvider,
  useAuth as useOidcAuth,
} from 'react-oidc-context';

import { setAuthToken } from '../../../../api/authToken';
import { setSessionExpiredHandler } from '../../../../api/sessionExpiry';
import { AuthContext } from '../../core/AuthContext';
import { locationPath } from '../../core/returnTo';
import { AUTH_MODES } from '../../core/authModes';
import {
  oidcClientId,
  oidcRedirectUri as configuredRedirectUri,
} from '../../../../runtimeConfig';

/* the configured value pins the URI registered at the issuer;
   without one, the app's own origin serves any origin setup */
const oidcRedirectUri = () => configuredRedirectUri() || window.location.origin;

/*
 * How long the silent renew may take before the reader is sent to the
 * login instead. The library has a timeout of its own (see
 * `silentRequestTimeoutInSeconds`); this one is the backstop for the
 * case it does not fire — an issuer that answers neither yes nor no
 * would otherwise leave the lock below closed for good, and with it
 * every later 401 unanswered, which is what a hanging page looks like.
 * Both are short: this time is time the reader spends looking at a
 * page that does nothing.
 */
const SILENT_RENEW_TIMEOUT_SECONDS = 5;
const RENEW_BACKSTOP_MS = (SILENT_RENEW_TIMEOUT_SECONDS + 3) * 1000;

/*
 * The token counts as spent this much before its stated end: the
 * clocks of browser and server differ, and a token the server has
 * already buried must not look alive here.
 */
const CLOCK_SKEW_SECONDS = 60;

/*
 * Whether a 401 is about the session at all. It is not, whenever the
 * token is still good: the backend answers 401 for reasons of its own
 * — an assumed role it will not grant, for one — and renewing a
 * healthy token changes nothing about those. Doing it anyway, once per
 * repetition of the same request, is how a page comes to load for
 * ever; such a 401 belongs to the request that provoked it and
 * surfaces there. An unreadable session counts as spent, so a session
 * that really has ended is still renewed.
 */
const sessionIsSpent = (user: User | null | undefined) => {
  if (!user) return true;
  if (user.expired) return true;
  const expiresAt = Number(user.expires_at);
  if (!Number.isFinite(expiresAt)) return true;
  return expiresAt - CLOCK_SKEW_SECONDS <= Date.now() / 1000;
};

const withBackstop = <T,>(promise: Promise<T>) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(
        () => reject(new Error('silent renew did not answer')),
        RENEW_BACKSTOP_MS
      );
    }),
  ]).finally(() => clearTimeout(timer));
};

const KeycloakAuthBridge = ({ children }: { children: React.ReactNode }) => {
  const auth = useOidcAuth();
  const queryClient = useQueryClient();
  const renewingRef = useRef(false);

  const token = auth.user?.access_token;

  /* set while rendering, not from an effect: the effects of the
     children run first, so a component mounting in this same commit
     would fire its request before the token is anywhere (see
     api/authToken.ts) */
  setAuthToken(token);

  /* a 401 from the API: try a silent renew once, otherwise a fresh
     login is needed — redirect and come back to the current page */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (renewingRef.current) return;
      if (!sessionIsSpent(auth.user)) return;
      renewingRef.current = true;
      withBackstop(auth.signinSilent())
        .then((user) => {
          if (!user) throw new Error('silent renew failed');
          /* the queries that met the 401 hold their error and nothing
             asks again by itself: without this the page keeps showing
             the failure although the session is good again */
          void queryClient.invalidateQueries();
        })
        .catch(() =>
          auth.signinRedirect({
            state: { returnTo: locationPath(window.location) },
          })
        )
        .finally(() => {
          renewingRef.current = false;
        });
    });
    return () => setSessionExpiredHandler(null);
  }, [auth, queryClient]);

  /* the background renewal gave up (e.g. the Keycloak session ended) */
  useEffect(
    () =>
      auth.events.addSilentRenewError(() =>
        auth.signinRedirect({
          state: { returnTo: locationPath(window.location) },
        })
      ),
    [auth]
  );

  const value = useMemo(
    () => ({
      authMode: AUTH_MODES.KEYCLOAK,
      user: auth.user?.profile,
      token: auth.user?.access_token,
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      login: (args?: Record<string, unknown>) => auth.signinRedirect(args),
      logout: () =>
        auth.signoutRedirect({
          post_logout_redirect_uri: oidcRedirectUri(),
        }),
      error: auth.error,
    }),
    [auth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const KeycloakAuthProvider = ({
  authority,
  children,
}: {
  authority: string;
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();

  /*
														      The oidcConfig takes care of silent refresh settings:
														            - automaticSilentRenew
															          - silent_redirect_uri
																        Without these settings,
																	      the library cannot renew tokens in the background,
																	            so authentication drops when the token expires.
																		          */
  const oidcConfig = {
    authority,
    /* AuthProvider mounts this only with a client id configured */
    client_id: oidcClientId() ?? '',
    redirect_uri: oidcRedirectUri(),
    silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
    automaticSilentRenew: true,
    silentRequestTimeoutInSeconds: SILENT_RENEW_TIMEOUT_SECONDS,
    onSigninCallback: (user: User | void) => {
      const state = user?.state as { returnTo?: string } | undefined;
      const returnTo = state?.returnTo || '/dashboard/hosting';
      void navigate(returnTo, { replace: true });
    },
    monitorSession: true,
  };

  return (
    <OidcProvider {...oidcConfig}>
      <KeycloakAuthBridge>{children}</KeycloakAuthBridge>
    </OidcProvider>
  );
};
